import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, branch, renderNothing } from 'recompose';
import { components as clientComponents } from 'mage-client';
import { analytics } from 'moet-core';
import { translator } from 'mage-i18n';

import { CAPS } from '@/constants';
import { openApiKeyDialog, closeApiKeyDialog, downloadApiKey } from '@/actions/credentials';
import { getCredentials, getDialogState } from '@/selectors/credentials';
import { getUserMerchantId, merchantRegion } from '@/selectors/merchant';
import { isGeneratingCredentials } from '@/selectors/loadingStates';
import ActionCard from '@/components/ActionCard';
import {
  isGenerateCredentialsButtonDisabled,
  isGenerateCredentialsButtonLoading,
} from '@/selectors/buttonStates';

import TranslatedButtonPrimary from '../TranslatedButtonPrimary';
import ApiKeyDialog from './ApiKeyDialog';
import { ANALYTICS_EVENTS } from './analyticsEvents';

const ApiKeyContainer = ({
  apiKey,
  buttonProps = {},
  generateCredentials,
  isDialogOpen,
  isNewLayout = false,
  merchantId,
  merchantRegion,
  onClose,
  onDownload,
  openDialog,
  t,
  tPrefix,
  isGenerateCredentialsButtonDisabled: disabled = false,
  isGenerateCredentialsButtonLoading: isGeneratingCredentials = false,
}) => {
  const generateCredentialsFn = async () => {
    await generateCredentials({ params: { mid: merchantId }, region: merchantRegion });
    analytics.logEvent(ANALYTICS_EVENTS.generateCredentials);
    openDialog();
  };
  return (
    <div>
      {isDialogOpen && !isGeneratingCredentials ? (
        <ApiKeyDialog
          apiKey={apiKey}
          onClose={onClose}
          onDownload={onDownload}
          merchantId={merchantId}
        />
      ) : null}
      {isNewLayout ? (
        <ActionCard
          title={t(`${tPrefix}.actions.connect_store.title`)}
          subtitle={t(`${tPrefix}.actions.connect_store.subtitle`)}
          buttonText={t(`${tPrefix}.actions.connect_store.button_text`)}
          buttonType="primary"
          onClick={generateCredentialsFn}
          buttonProps={buttonProps}
          disabled={disabled}
          loading={isGeneratingCredentials}
        />
      ) : (
        <TranslatedButtonPrimary
          tKey={`${tPrefix}.main.number_of_live_keys_in_total.button`}
          onClick={generateCredentialsFn}
          disabled={disabled}
          loading={isGeneratingCredentials}
        />
      )}
    </div>
  );
};

ApiKeyContainer.propTypes = {
  apiKey: PropTypes.object.isRequired,
  generateCredentials: PropTypes.func.isRequired,
  isDialogOpen: PropTypes.bool.isRequired,
  isGenerateCredentialsButtonDisabled: PropTypes.bool.isRequired,
  isGenerateCredentialsButtonLoading: PropTypes.bool.isRequired,
  merchantId: PropTypes.string.isRequired,
  merchantRegion: PropTypes.string.isRequired,
  openDialog: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  tPrefix: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  buttonProps: PropTypes.object,
  isNewLayout: PropTypes.bool,
};

export default compose(
  connect(
    state => ({
      apiKey: getCredentials(state),
      merchantId: getUserMerchantId(state),
      merchantRegion: merchantRegion(state),
      isDialogOpen: getDialogState(state),
      isGeneratingCredentials: isGeneratingCredentials(state),
      t: translator(state),
      isGenerateCredentialsButtonDisabled: isGenerateCredentialsButtonDisabled(state),
      isGenerateCredentialsButtonLoading: isGenerateCredentialsButtonLoading(state),
    }),
    {
      openDialog: openApiKeyDialog,
      onClose: closeApiKeyDialog,
      onDownload: downloadApiKey,
    },
  ),
  clientComponents.withPromiseBasedCapabilities({
    generateCredentials: CAPS.MERCHANTS_CREDENTIALS_POST,
  }),
  branch(({ generateCredentials }) => !generateCredentials, renderNothing),
)(ApiKeyContainer);
